import { useEffect, useMemo, useState } from 'react'
import { api, fmtDate, fmtMoney } from '../lib/api'
import type { DossierAnnuel, Ecriture } from '../lib/api'
import { href, navigate } from '../lib/router'
import { IconArrowLeft, IconSearch } from '../components/Icons'

const PAGE_SIZE = 50

type State = {
  dossier: DossierAnnuel | null
  ecritures: Ecriture[] | null
  loading: boolean
  error: string | null
}

export function DossierAnnuelPage({ code, dossierId }: { code: string; dossierId: string }) {
  const [state, setState] = useState<State>({
    dossier: null, ecritures: null, loading: true, error: null
  })
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const [journalFilter, setJournalFilter] = useState<string>('')

  useEffect(() => {
    let alive = true
    setState({ dossier: null, ecritures: null, loading: true, error: null })
    Promise.all([api.dossiersAnnuels(code), api.ecritures(code, dossierId)]).then(([dosList, ecr]) => {
      if (!alive) return
      const dossier = dosList.ok
        ? dosList.data.find(d => String(d.IdDossierAnnuel) === String(dossierId)) ?? null
        : null
      if (!ecr.ok) {
        setState({ dossier, ecritures: null, loading: false, error: ecr.error })
        return
      }
      setState({ dossier, ecritures: ecr.data, loading: false, error: null })
    })
    return () => { alive = false }
  }, [code, dossierId])

  const journaux = useMemo(() => {
    if (!state.ecritures) return []
    return Array.from(new Set(state.ecritures.map(e => e.JournalCode))).sort()
  }, [state.ecritures])

  const filtered = useMemo(() => {
    if (!state.ecritures) return []
    const q = query.trim().toLowerCase()
    return state.ecritures.filter(e => {
      if (journalFilter && e.JournalCode !== journalFilter) return false
      if (!q) return true
      return (
        e.EcritureLib?.toLowerCase().includes(q) ||
        e.CompteNum?.toLowerCase().includes(q) ||
        e.CompteLib?.toLowerCase().includes(q) ||
        e.PieceRef?.toLowerCase().includes(q) ||
        e.EcritureNum?.toLowerCase().includes(q)
      )
    })
  }, [state.ecritures, query, journalFilter])

  const pageStart = page * PAGE_SIZE
  const pageEnd = Math.min(pageStart + PAGE_SIZE, filtered.length)
  const pageItems = filtered.slice(pageStart, pageEnd)
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))

  useEffect(() => { setPage(0) }, [query, journalFilter])

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, e) => ({
        debit: acc.debit + (Number(e.Debit) || 0),
        credit: acc.credit + (Number(e.Credit) || 0),
      }),
      { debit: 0, credit: 0 }
    )
  }, [filtered])

  return (
    <>
      <button
        type="button"
        className="btn-back"
        onClick={() => navigate(`/societes/${encodeURIComponent(code)}`)}
      >
        <IconArrowLeft size={16} />
        <span>Société {code}</span>
      </button>

      {state.loading && <div className="empty-state"><p>Chargement…</p></div>}

      {state.dossier && (
        <div className="detail-header">
          <h2 className="detail-title">{state.dossier.Libelle}</h2>
          <div className="detail-meta">
            <span className="chip">
              Exercice {fmtDate(state.dossier.DateDebut)} → {fmtDate(state.dossier.DateFin)}
            </span>
            {state.dossier.EnCours === 'true' && <span className="chip chip-green">En cours</span>}
            {state.dossier.DateCloture && <span className="chip chip-grey">Clôturé le {fmtDate(state.dossier.DateCloture)}</span>}
          </div>
        </div>
      )}

      {state.error && (
        <div className="empty-state">
          <h2>Erreur de chargement</h2>
          <p>{state.error}</p>
        </div>
      )}

      {state.ecritures && (
        <>
          <div className="card-stat-grid">
            <div className="card-stat">
              <p className="stat-label">Écritures</p>
              <p className="stat-value">{filtered.length.toLocaleString('fr-FR')}</p>
            </div>
            <div className="card-stat">
              <p className="stat-label">Débit total</p>
              <p className="stat-value stat-value-mono">{fmtMoney(totals.debit)}</p>
            </div>
            <div className="card-stat">
              <p className="stat-label">Crédit total</p>
              <p className="stat-value stat-value-mono">{fmtMoney(totals.credit)}</p>
            </div>
            <div className="card-stat">
              <p className="stat-label">Solde</p>
              <p className="stat-value stat-value-mono">{fmtMoney(totals.debit - totals.credit)}</p>
            </div>
          </div>

          <div className="filter-bar">
            <label className="filter-input">
              <IconSearch size={16} />
              <input
                type="search"
                placeholder="Rechercher (libellé, compte, pièce…)"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </label>
            {journaux.length > 0 && (
              <label className="filter-select">
                <span>Journal</span>
                <select value={journalFilter} onChange={e => setJournalFilter(e.target.value)}>
                  <option value="">Tous</option>
                  {journaux.map(j => <option key={j} value={j}>{j}</option>)}
                </select>
              </label>
            )}
          </div>

          {state.ecritures.length === 0 && (
            <div className="empty-state">
              <h2>Dossier vide</h2>
              <p>Aucune écriture n'a encore été saisie sur ce dossier annuel.</p>
              <p><a href={href('/societes')}>← Retour au portefeuille</a></p>
            </div>
          )}

          {filtered.length > 0 && (
            <>
              <div className="table-wrap">
                <table className="table-ecritures">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Journal</th>
                      <th>Pièce</th>
                      <th>Compte</th>
                      <th>Libellé</th>
                      <th className="align-right">Débit</th>
                      <th className="align-right">Crédit</th>
                      <th>Let.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((e, i) => (
                      <tr key={e.IdEcriture ?? `${pageStart + i}`}>
                        <td className="mono">{fmtDate(e.EcritureDate)}</td>
                        <td><span className="chip-journal">{e.JournalCode}</span></td>
                        <td className="mono">{e.PieceRef ?? '—'}</td>
                        <td className="mono">
                          {e.CompteNum}
                          {e.CompAuxNum && <span className="compte-aux"> / {e.CompAuxNum}</span>}
                        </td>
                        <td>{e.EcritureLib}</td>
                        <td className="align-right mono">{e.Debit ? fmtMoney(e.Debit) : ''}</td>
                        <td className="align-right mono">{e.Credit ? fmtMoney(e.Credit) : ''}</td>
                        <td className="mono">{e.EcritureLet ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <nav className="pagination" aria-label="Pagination des écritures">
                <button type="button" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                  Précédent
                </button>
                <span className="pagination-info">
                  {pageStart + 1}–{pageEnd} sur {filtered.length}
                </span>
                <button type="button" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                  Suivant
                </button>
              </nav>
            </>
          )}
        </>
      )}
    </>
  )
}
